import { NO_PATTERN_BUSINESS_LOGIC } from '@vfde-brix/ws10/core';
import {
    createItemSummaryCard,
    ItemSummaryCard,
} from '@vfde-brix/ws10/item-summary-card';
import { ADDITIONAL_PAGE_OPTIONS } from '@vfde-sails/constants';
import { getTauschbonusAmount } from '@vfde-sails/page-options';
import { replacePlaceholders } from '@vfde-sails/utils';
import {
    RootState,
    useAppDispatch,
} from '../../../app/store';
import { startAppListening } from '../../../app/listener';
import getTauschbonusSubline from '../../../helpers/getTauschbonusSubline';
import { selectCustomerConfig } from '../../app/slice';
import { selectSelectedTradeInDevice } from '../selectors';
import {
    deleteSelectedTradeInDevice,
    setInitialState,
    selectIsTradeInSelected,
} from '../slice';

/**
 * Mounts the tradeIn summary card
 * for the selected device
 */
export const mountTradeInItemSummaryCard = (containerId: string): ItemSummaryCard | null => {
    const containerElement = document.getElementById(containerId);

    /* istanbul ignore if */
    if (!containerElement) {
        return null;
    }

    const dispatch = useAppDispatch();

    const onDeleteButtonClick = () => {
        dispatch(deleteSelectedTradeInDevice());
    };

    const itemSummaryCard = createItemSummaryCard(containerElement, {
        onDeleteButtonClick,
    });

    listenForUpdates(itemSummaryCard);

    return itemSummaryCard;
};

const listenForUpdates = (itemSummaryCard: ItemSummaryCard) => {
    startAppListening({
        actionCreator: setInitialState,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            updateItemSummaryCard(itemSummaryCard, state);
        },
    });

    startAppListening({
        predicate: (_action, currentState, previousState) =>
            selectCustomerConfig(currentState).isHardwareOnly !== selectCustomerConfig(previousState).isHardwareOnly
            || selectIsTradeInSelected(currentState) !== selectIsTradeInSelected(previousState)
            || selectSelectedTradeInDevice(currentState) !== selectSelectedTradeInDevice(previousState)
        ,
        effect: (_action, listenerApi) => {
            const state = listenerApi.getState();
            updateItemSummaryCard(itemSummaryCard, state);
        },
    });
};

/**
 * Updates the ItemSummaryCard for tradeIn with the given state
 */
const updateItemSummaryCard = (itemSummaryCard: ItemSummaryCard, state: RootState) => {
    const customerConfig = selectCustomerConfig(state);
    const { isHardwareOnly } = customerConfig;
    const isTradeInSelected = selectIsTradeInSelected(state);
    const selectedTradeInDevice = selectSelectedTradeInDevice(state);
    const shouldShowItemSummaryCard = !isHardwareOnly && isTradeInSelected && !!selectedTradeInDevice;

    if (!shouldShowItemSummaryCard) {
        itemSummaryCard.toggleContainer(true);

        return;
    }

    const { tradeIn: tradeInOptions, tauschbonus: tauschbonusOptions } = window[ADDITIONAL_PAGE_OPTIONS].promos || {};
    const tauschbonusValue = getTauschbonusAmount(customerConfig.deviceId!);

    const tauschbonusDescription = (tauschbonusOptions && tauschbonusValue) ? getTauschbonusSubline(
        tauschbonusOptions.tauschbonusSubline,
        tauschbonusValue,
    ) : undefined;

    const subline = replacePlaceholders(
        tradeInOptions?.itemSummaryCardSubline || '',
        {
            maxPrice: selectedTradeInDevice.formattedPrice || '',
        },
    );

    const { imgSrc } = selectedTradeInDevice;

    const existingItemSummaryCardItemProps = itemSummaryCard.getProperties();

    itemSummaryCard.update({
        items: [
            {
                ...existingItemSummaryCardItemProps.items?.[0] || {},
                image: {
                    imgSrcDesktop: imgSrc,
                    imgSrcMobile: imgSrc,
                    stdImgAlt: '',
                },
                stdHeadline: selectedTradeInDevice.name,
                stdSubline: subline,
                containerCopytext: tauschbonusDescription,
                business: NO_PATTERN_BUSINESS_LOGIC,
            },
        ],
    });

    itemSummaryCard.toggleContainer(false);
};
